import { CLOSED_CLAIM_STATUSES } from "./claimsShared";

export const CLAIM_TYPE_TO_API = {
  Damage: "DAMAGE",
  Shortage: "SHORTAGE",
  Loss: "LOSS",
  Delay: "DELAY",
  "Service Failure": "SERVICE_FAILURE",
};

export const CLAIM_TYPE_FROM_API = Object.fromEntries(
  Object.entries(CLAIM_TYPE_TO_API).map(([label, value]) => [value, label])
);

export const CLAIM_STATUS_TO_API = {
  Draft: "DRAFT",
  Submitted: "SUBMITTED",
  "Cargo Release Signed (Payment Pending)": "CARGO_RELEASE_SIGNED_PAYMENT_PENDING",
  "Partial Payment Received": "PARTIAL_PAYMENT_RECEIVED",
  "Claim Closed – Paid in Full": "CLOSED_PAID_IN_FULL",
  "Claim Closed – Claim Resolved": "CLOSED_RESOLVED",
  "Claim Closed – Claim Rejected": "CLOSED_REJECTED",
};

export const CLAIM_STATUS_FROM_API = Object.fromEntries(
  Object.entries(CLAIM_STATUS_TO_API).map(([label, value]) => [value, label])
);

export const claimTypeToApi = (label) => CLAIM_TYPE_TO_API[label] || label;
export const claimTypeFromApi = (value) => CLAIM_TYPE_FROM_API[value] || value;

export const claimStatusToApi = (label) => CLAIM_STATUS_TO_API[label] || label;
export const claimStatusFromApi = (value, statusLabel) => {
  if (value && CLAIM_STATUS_FROM_API[value]) {
    return CLAIM_STATUS_FROM_API[value];
  }
  return statusLabel || value;
};

export const isDraftClaim = (claim) =>
  claim?.statusEnum === "DRAFT" || claim?.status === "Draft";

export const isOpenClaim = (claim) => {
  const status = claim?.status;
  if (!status || status === "Draft") return false;
  return !CLOSED_CLAIM_STATUSES.has(status);
};

export const formatStopLabel = (stop, index = 0) => {
  const type = (stop.stopType || "").replace(/_/g, " ");
  const typeLabel =
    type === "DROP OFF" ? "Delivery" : type.charAt(0) + type.slice(1).toLowerCase();
  return `Stop ${stop.stopNumber ?? index + 1} — ${typeLabel} (${stop.stopName || stop.warehouseName || "—"})`;
};

export const buildStopsFromShipment = (shipmentDetails) => {
  const stops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? a.stopNumber ?? 0) - (b.stopSequence ?? b.stopNumber ?? 0)
  );
  return stops
    .filter((stop) => stop.id != null)
    .map((stop, index) => ({
    id: stop.id,
    value: String(stop.id),
    label: formatStopLabel(stop, index),
  }));
};

export const buildShipmentContextFromDetails = (shipmentDetails) => {
  const stops = buildStopsFromShipment(shipmentDetails);
  const sortedStops = [...(shipmentDetails?.shipmentStops || [])].sort(
    (a, b) => (a.stopSequence ?? a.stopNumber ?? 0) - (b.stopSequence ?? b.stopNumber ?? 0)
  );
  const origin =
    sortedStops[0]?.stopName ||
    sortedStops[0]?.warehouseName ||
    shipmentDetails?.portOfOrigin ||
    "—";
  const destination =
    sortedStops[sortedStops.length - 1]?.stopName ||
    sortedStops[sortedStops.length - 1]?.warehouseName ||
    shipmentDetails?.portOfDestination ||
    "—";

  return {
    shipmentNumber: shipmentDetails?.shipmentNumber || "",
    poNumber: shipmentDetails?.poNumber || "",
    origin,
    destination,
    carrierName: shipmentDetails?.carrierName || "—",
    stops,
    officeCode: shipmentDetails?.officeCode || "",
  };
};

export const mapSummaryDto = (dto, officeCode) => ({
  id: dto.claimNumber,
  claimNumber: dto.claimNumber,
  shipmentNumber: dto.shipmentNumber,
  poNumber: dto.poNumber,
  claimType: claimTypeFromApi(dto.claimType),
  status: claimStatusFromApi(dto.status, dto.statusLabel),
  statusEnum: dto.status,
  relatedStop: dto.relatedStopLabel || "",
  dateFiled: dto.dateFiled,
  totalClaimAmount: dto.totalClaimAmount,
  amountApproved: dto.amountApproved,
  amountPaid: dto.amountPaid,
  shipmentOfficeCode: officeCode || dto.shipmentOfficeCode,
  officeCode: officeCode || dto.officeCode,
});

export const mapDetailsDto = (dto, documents = []) => ({
  id: dto.claimNumber,
  claimNumber: dto.claimNumber,
  shipmentNumber: dto.shipmentNumber,
  poNumber: dto.poNumber,
  claimType: claimTypeFromApi(dto.claimType),
  status: claimStatusFromApi(dto.status, dto.statusLabel),
  statusEnum: dto.status,
  relatedStopId: dto.relatedStopId,
  relatedStop: dto.relatedStopLabel || "",
  dateIssueOccurred: dto.dateIssueOccurred || "",
  dateFiled: dto.dateFiled || "",
  description: dto.description || "",
  totalClaimAmount: dto.totalClaimAmount,
  additionalCosts: dto.additionalCosts,
  amountApproved: dto.amountApproved,
  amountPaid: dto.amountPaid,
  carrierName: dto.carrierName || "—",
  origin: dto.originLabel || "",
  destination: dto.destinationLabel || "",
  supportingDocuments: (documents || []).map(mapDocumentDto),
});

export const mapDocumentDto = (doc) => ({
  id: String(doc.id),
  documentId: doc.id,
  uuidFilename: doc.uuidFilename,
  name: doc.documentName,
  size: doc.fileSize ?? null,
  file: null,
  isExisting: true,
});

export const mapKpiSummary = (dto) => ({
  openClaims: dto?.openClaimCount ?? 0,
  pendingPayment: Number(dto?.pendingPaymentTotal ?? 0),
  paidLast90: Number(dto?.paidLast90DaysTotal ?? 0),
  avgDaysToClose: dto?.avgDaysToClose != null ? dto.avgDaysToClose : "—",
});

export const detailsToFormValues = (claim) => ({
  claimNumber: claim.claimNumber || "",
  claimType: claim.claimType || "Damage",
  dateIssueOccurred: claim.dateIssueOccurred || "",
  dateClaimFiled: claim.dateFiled || "",
  description: claim.description || "",
  totalClaimAmount:
    claim.totalClaimAmount != null ? String(claim.totalClaimAmount) : "",
  additionalCosts:
    claim.additionalCosts != null ? String(claim.additionalCosts) : "",
  amountApproved:
    claim.amountApproved != null ? String(claim.amountApproved) : "",
  amountPaid: claim.amountPaid != null ? String(claim.amountPaid) : "",
  status: claim.status || "Submitted",
  statusEnum: claim.statusEnum || "",
  relatedStopId: claim.relatedStopId ?? "",
  relatedStop: claim.relatedStop || "",
  shipmentNumber: claim.shipmentNumber || "",
  shipmentOfficeCode: claim.officeCode || claim.shipmentOfficeCode || "",
  poNumber: claim.poNumber || "",
  origin: claim.origin || "",
  destination: claim.destination || "",
  supportingDocuments: claim.supportingDocuments || [],
});

export const formToCreateRequest = (form, { parseCurrencyValue }) => ({
  // User-entered as of LGX-1047; trimmed to match the backend's normalization so the
  // duplicate check compares the same value the user sees.
  claimNumber: String(form.claimNumber ?? "").trim(),
  claimType: claimTypeToApi(form.claimType),
  relatedStopId:
    form.relatedStopId != null && form.relatedStopId !== ""
      ? Number(form.relatedStopId)
      : null,
  dateIssueOccurred: form.dateIssueOccurred || null,
  description: form.description || null,
  totalClaimAmount: parseCurrencyValue(form.totalClaimAmount),
  additionalCosts: parseCurrencyValue(form.additionalCosts),
});

export const formToUpdateRequest = (form, { parseCurrencyValue, includeStatus = false }) => {
  const relatedStopId =
    form.relatedStopId != null && form.relatedStopId !== ""
      ? Number(form.relatedStopId)
      : null;

  const payload = {
    claimType: claimTypeToApi(form.claimType),
    relatedStopId,
    dateIssueOccurred: form.dateIssueOccurred || null,
    description: form.description || null,
    totalClaimAmount: parseCurrencyValue(form.totalClaimAmount),
    additionalCosts: parseCurrencyValue(form.additionalCosts),
  };

  if (includeStatus && form.status) {
    payload.status = claimStatusToApi(form.status);
  }
  if (form.amountApproved !== "" && form.amountApproved != null) {
    payload.amountApproved = parseCurrencyValue(form.amountApproved);
  }
  if (form.amountPaid !== "" && form.amountPaid != null) {
    payload.amountPaid = parseCurrencyValue(form.amountPaid);
  }

  return payload;
};

export const getApiErrorMessage = (error, fallback = "Request failed.") =>
  error?.response?.data?.content ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;
